import React, { useEffect, useState } from 'react';
import { Alert, Button, Select, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { fetchSupplierOrders, fetchSupplierOrder, getSupplierOrderDisplayNumber } from '../../orders/api/supplierOrdersApi';
import { fetchOrders, fetchOrderDetail } from '../../orders/api/ordersApi';
import { buildPurchaseSourceLineCandidates } from '../purchaseInvoiceCandidates';
import { buildSalesSourceLineCandidates } from '../salesInvoiceCandidates';
import { coverageMarkerLabel, coverageStateLabel, formatRemainderLinesInSentence } from '../invoiceCoverageLabels';
import type { InvoiceCoverage } from '../../../types/invoiceCoverage';
import type { WorkflowInvoiceDirection, WorkflowInvoiceSourceLineCandidate } from '../../../types/workflowInvoice';

type OrderOption = { value: string; label: string; supplierId?: string; coverageState: string | null };
type SelectedCoverage = { id: string; orderNumber: string; coverage: InvoiceCoverage | null };
interface Props {
  direction: WorkflowInvoiceDirection;
  ids: string[];
  disabled?: boolean;
  onChange: (ids: string[], supplierId?: string) => void;
  onLoading?: (loading: boolean) => void;
  onCandidates: (candidates: WorkflowInvoiceSourceLineCandidate[]) => void;
  /**
   * The draft is a correction invoice (WP2/WP15): it states the corrected
   * invoice, not a new share of the order, so the uninvoiced remainder, its
   * prefill and the over-invoicing hints do not apply (the backend checks the
   * corrected quantities at approval).
   */
  correction?: boolean;
}

// Candidates without the uninvoiced remainder (label, prefill, over-remainder hint).
function withoutRemainder(candidate: WorkflowInvoiceSourceLineCandidate): WorkflowInvoiceSourceLineCandidate {
  const { remainingQuantity: _quantity, remainingUnit: _unit, remainingQuantitySet: _set, ...rest } = candidate;
  return rest;
}

/**
 * Source ZK/ZD of an invoice draft. WP11 (docs/decisions/2026-09-24-wp11-
 * faktury-czesciowe.md): an order may carry N invoices, so a partially
 * invoiced order stays selectable (marked „Zafakturowane częściowo”) and the
 * selected order's uninvoiced remainder is shown and prefilled into linked
 * lines. A fully invoiced order is hidden from the choice (over-invoicing is
 * an explicit exception, not a default path) unless it is already selected.
 */
export default function InvoiceOrderPicker({ direction, ids, disabled, onChange, onCandidates, onLoading, correction = false }: Props) {
  const { t } = useTranslation('invoices');
  const [options, setOptions] = useState<OrderOption[]>([]);
  const [selected, setSelected] = useState<SelectedCoverage[]>([]);
  const [loading, setLoading] = useState(false);
  const [listFailed, setListFailed] = useState(false);
  const [linesFailed, setLinesFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setListFailed(false);
    const request = direction === 'PURCHASE'
      ? fetchSupplierOrders().then(rows => rows.map(row => ({ value: row.id, label: [getSupplierOrderDisplayNumber(row), row.supplier?.name].filter(Boolean).join(' — '), supplierId: row.supplierId, coverageState: row.invoiceCoverageState ?? null })))
      : fetchOrders().then(rows => rows.map(row => ({ value: row.id, label: [row.displayOrderNumber, row.customerName].filter(Boolean).join(' — '), coverageState: row.invoiceCoverageState ?? null })));
    request.then(rows => { if (active) setOptions(rows); }).catch(() => { if (active) setListFailed(true); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [direction, retry]);
  const idsKey = ids.join(',');
  useEffect(() => {
    let active = true;
    onCandidates([]);
    setSelected([]);
    onLoading?.(ids.length > 0);
    setLinesFailed(false);
    const request: Promise<{ candidates: WorkflowInvoiceSourceLineCandidate[]; coverage: SelectedCoverage[] }> = direction === 'PURCHASE'
      ? Promise.all(ids.map(id => fetchSupplierOrder(id))).then(rows => ({
        candidates: rows.flatMap(buildPurchaseSourceLineCandidates),
        coverage: rows.map(row => ({ id: row.id, orderNumber: getSupplierOrderDisplayNumber(row), coverage: row.invoiceCoverage ?? null })),
      }))
      : Promise.all(ids.map(id => fetchOrderDetail(id))).then(rows => ({
        candidates: rows.flatMap(buildSalesSourceLineCandidates),
        coverage: rows.map(row => ({ id: row.id, orderNumber: row.displayOrderNumber, coverage: row.invoiceCoverage ?? null })),
      }));
    request.then(result => { if (active) { onCandidates(correction ? result.candidates.map(withoutRemainder) : result.candidates); setSelected(result.coverage); setLinesFailed(false); } }).catch(() => { if (active) { onCandidates([]); setLinesFailed(true); } }).finally(() => { if (active) onLoading?.(false); });
    return () => { active = false; };
    // Candidate refresh belongs to the selected source identities, not form edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction, idsKey, retry, correction]);
  const supplier = options.find(option => ids.includes(option.value))?.supplierId;
  const labelOf = (option: OrderOption) => {
    const marker = coverageStateLabel(option.coverageState, t);
    return marker ? `${option.label} — ${marker}` : option.label;
  };
  const choices = options
    .filter(option => option.coverageState !== 'FULL' || ids.includes(option.value))
    .map(option => ({ value: option.value, label: labelOf(option), disabled: Boolean(supplier && option.supplierId && supplier !== option.supplierId) }));
  const invoiced = correction ? [] : selected.filter(entry => entry.coverage && entry.coverage.state !== 'NONE');
  return <Space direction="vertical" style={{ width: '100%' }}>
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>{t(direction === 'PURCHASE' ? 'workflow.page.sourceOrdersHint' : 'workflow.page.salesSourceOrdersHint')}</Typography.Text>
    <Select
      mode={direction === 'PURCHASE' ? 'multiple' : undefined}
      labelRender={({ value }) => choices.find(option => option.value === value)?.label || t('workflow.page.sourceOrder')}
      showSearch optionFilterProp="label" allowClear loading={loading} disabled={disabled}
      aria-label={t('workflow.page.sourceOrders')}
      placeholder={t('workflow.page.sourceOrders')}
      style={{ width: '100%' }}
      value={direction === 'PURCHASE' ? ids : ids[0]}
      options={choices}
      onChange={(value: string[] | string | undefined) => {
        const next = Array.isArray(value) ? value : value ? [value] : [];
        onChange(next, options.find(option => next.includes(option.value))?.supplierId);
      }}
    />
    {invoiced.map(entry => {
      const coverage = entry.coverage!;
      const remainder = coverage.lines.filter(line => line.remainingQuantity == null || line.remainingQuantity > 0);
      return <Alert key={entry.id} type={coverage.state === 'FULL' ? 'warning' : 'info'} showIcon data-testid="invoice-order-coverage"
        message={`${entry.orderNumber}: ${coverageMarkerLabel(coverage, t)}`}
        description={coverage.state === 'FULL'
          ? t('workflow.coverage.fullHint')
          : t('workflow.coverage.remainderHint', { lines: formatRemainderLinesInSentence(remainder) })} />;
    })}
    {ids.length > 0 && !listFailed && !linesFailed && <Button type="link" onClick={() => setRetry(value => value + 1)}>{t('workflow.page.retrySources')}</Button>}
    {(listFailed || linesFailed) && <Alert type="warning" showIcon message={t(listFailed ? 'workflow.page.ordersLoadFailed' : 'workflow.page.linesLoadFailed')} action={<Button onClick={() => setRetry(value => value + 1)}>{t('workflow.page.retrySources')}</Button>} />}
  </Space>;
}
